// Shared attachment helpers used by both the Compose page and the
// inline reply panel on the inbox detail page. Centralising the file
// -> base64 conversion, the 25 MB combined-size limit, and the byte
// formatter keeps the two upload UIs in lockstep so a fix or limit
// change only has to happen once.

export interface ComposeAttachment {
  filename: string;
  /** Canonical (no whitespace, no `data:` prefix) base64 string. */
  content: string;
  /** Browser-reported MIME type, when available. */
  contentType: string | null;
  /** Raw byte size — used for the running total + UI display only. */
  size: number;
}

// The /api/send endpoint caps combined raw attachment size at 25 MB
// (Resend's hard limit is ~40 MB; the headroom leaves space for the
// rest of the JSON payload). Mirroring the limit on the client lets us
// reject obviously-too-large files before we waste time base64-encoding
// them and round-tripping the request.
export const ATTACHMENT_TOTAL_LIMIT_BYTES = 25 * 1024 * 1024;

// The shared `<input type="file">` `accept=` filter. The server
// allowlists PDF + the common web image formats (PNG, JPEG, GIF, WebP)
// via per-format magic-byte checks, so we narrow the picker to those
// types too. Drag-and-drop still routes everything through the server
// validator, which surfaces a friendly 415 if an unsupported file
// somehow makes it through.
export const ATTACHMENT_ACCEPT =
  "application/pdf,.pdf,image/png,.png,image/jpeg,.jpg,.jpeg,image/gif,.gif,image/webp,.webp";

export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error("read failed"));
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== "string") {
        reject(new Error("unexpected reader result"));
        return;
      }
      // FileReader.readAsDataURL produces "data:<mime>;base64,<payload>".
      // We only need the payload — the server reconstructs everything else
      // from the explicit `filename` + `contentType` fields.
      const comma = result.indexOf(",");
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.readAsDataURL(file);
  });
}

export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

// True when the attachment's MIME type is one of the image formats the
// upload widget accepts (PNG, JPEG, GIF, WebP). The contentType comes
// straight from the browser, so we just look for the `image/` prefix
// rather than enumerate — anything that survives the server's
// magic-byte allowlist will render fine in an <img>.
export function isImageAttachment(a: ComposeAttachment): boolean {
  return !!a.contentType && a.contentType.startsWith("image/");
}

// Build an object URL from an in-memory image attachment so the
// compose UI can show a real thumbnail without an extra upload
// round-trip. We decode the canonical base64 payload back into a Blob
// and hand that to URL.createObjectURL — much cheaper for the
// browser than embedding a multi-megabyte data: URL in the DOM.
//
// Returns null for non-image attachments and for payloads that fail
// to decode (atob throws on garbage input). Callers MUST revoke the
// returned URL via URL.revokeObjectURL when they're done with it,
// otherwise the underlying Blob is pinned in memory until the tab
// closes.
export function attachmentToObjectUrl(a: ComposeAttachment): string | null {
  if (!isImageAttachment(a) || !a.contentType) return null;
  try {
    const binary = atob(a.content);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    const blob = new Blob([bytes], { type: a.contentType });
    return URL.createObjectURL(blob);
  } catch {
    return null;
  }
}

export interface AddFilesResult {
  next: ComposeAttachment[];
  /** User-friendly error messages, one per skipped file. */
  errors: string[];
}

// Append `files` to an existing attachment list, enforcing the combined
// size limit. Skipped files (over-limit or unreadable) come back as
// errors so the caller can decide how to surface them (toast, inline
// text, etc.) — this helper never touches the DOM itself.
export async function addFiles(
  current: ComposeAttachment[],
  files: FileList | File[],
): Promise<AddFilesResult> {
  const list = Array.from(files);
  if (list.length === 0) return { next: current, errors: [] };
  const next: ComposeAttachment[] = [...current];
  const errors: string[] = [];
  let runningTotal = next.reduce((acc, a) => acc + a.size, 0);
  for (const file of list) {
    if (runningTotal + file.size > ATTACHMENT_TOTAL_LIMIT_BYTES) {
      errors.push(
        `"${file.name}" would push attachments past the 25 MB total limit.`,
      );
      continue;
    }
    try {
      const content = await fileToBase64(file);
      next.push({
        filename: file.name,
        content,
        contentType: file.type || null,
        size: file.size,
      });
      runningTotal += file.size;
    } catch {
      errors.push(`Could not read "${file.name}".`);
    }
  }
  return { next, errors };
}
